import { FC, useLayoutEffect, useRef, useState } from 'react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableTagsCellProps {
  tags: string[];
  /** Tailwind class for badge color (default: badge-amber) */
  badgeClassName?: string;
  /** Collapsed row height in pixels (default: 28 — fits one row of badges) */
  collapsedHeight?: number;
}

/**
 * Renders a list of tags as wrapping badges. When the wrapped layout would
 * overflow `collapsedHeight`, a chevron toggle appears that expands the cell
 * vertically to show every tag. State is local per row so each cell tracks
 * its own expanded/collapsed flag.
 */
export const ExpandableTagsCell: FC<ExpandableTagsCellProps> = ({
  tags,
  badgeClassName = 'badge-amber',
  collapsedHeight = 28,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      setOverflows(el.scrollHeight > collapsedHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tags, collapsedHeight]);

  if (!tags || tags.length === 0) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="flex items-start gap-1.5">
      <div
        ref={innerRef}
        className="flex flex-wrap gap-1 overflow-hidden transition-[max-height] duration-200 ease-out"
        style={{ maxHeight: expanded ? '1000px' : `${collapsedHeight}px` }}
      >
        {tags.map((tag, i) => (
          <Badge key={`${tag}-${i}`} className={badgeClassName}>
            {tag}
          </Badge>
        ))}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
          className="shrink-0 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title={expanded ? 'Collapse' : 'Show all tags'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
};

export default ExpandableTagsCell;
